import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ComboBoxSearch from "@/components/ComboBoxSearch";
import { Wallet, TrendingDown, TrendingUp, FileMinus, FilePlus, Receipt, IndianRupee, Trash2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
const ENTRY_TYPE_LABEL = {
  opening: "Opening",
  purchase: "Purchase (GRN)",
  purchase_reversal: "GRN Cancelled",
  payment: "Payment",
  payment_reversal: "Payment Cancelled",
  debit_note: "Debit Note",
  credit_note: "Credit Note",
  note_reversal: "Note Cancelled",
};

const ENTRY_TYPE_TONE = {
  opening: "bg-slate-100 text-slate-800 border-slate-200",
  purchase: "bg-amber-50 text-amber-800 border-amber-200",
  purchase_reversal: "bg-red-50 text-red-700 border-red-200",
  payment: "bg-emerald-50 text-emerald-800 border-emerald-200",
  payment_reversal: "bg-red-50 text-red-700 border-red-200",
  debit_note: "bg-blue-50 text-blue-800 border-blue-200",
  credit_note: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  note_reversal: "bg-red-50 text-red-700 border-red-200",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function VendorLedger() {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [entryType, setEntryType] = useState("all");
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState({ items: [], total: 0, closing_balance: 0 });
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(null); // "debit" | "credit" | null

  useEffect(() => {
    api.get("/production/rm-vendors").then((r) => setVendors(r.data || []));
  }, []);

  const load = useCallback(async () => {
    if (!vendorId) {
      setBalance(null);
      setLedger({ items: [], total: 0, closing_balance: 0 });
      setOutstanding([]);
      return;
    }
    setLoading(true);
    try {
      const params = { vendor_id: vendorId, page: 1, page_size: 500 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (entryType !== "all") params.entry_type = entryType;
      const [b, l, o] = await Promise.all([
        api.get(`/production/rm-vendors/${vendorId}/balance`),
        api.get("/production/rm-vendor-ledger", { params }),
        api.get(`/production/rm-vendors/${vendorId}/outstanding-invoices`),
      ]);
      setBalance(b.data);
      setLedger(l.data);
      setOutstanding(o.data);
    } finally {
      setLoading(false);
    }
  }, [vendorId, dateFrom, dateTo, entryType]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* ---------- Toolbar ---------- */}
      <Surface className="!p-3 flex flex-wrap items-center gap-3" data-testid="vendor-ledger-toolbar">
        <div className="min-w-[280px] flex-1">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Vendor</p>
          <ComboBoxSearch
            value={vendorId}
            onChange={setVendorId}
            options={vendors.map((v) => ({ value: v.id, label: v.name, hint: v.mobile || v.gst_number }))}
            placeholder="Pick a vendor to view ledger…"
            clearable
            testId="ledger-vendor"
          />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Date From</p>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 rounded-md border border-gray-200 px-2 text-sm" data-testid="ledger-date-from" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Date To</p>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 rounded-md border border-gray-200 px-2 text-sm" data-testid="ledger-date-to" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Entry Type</p>
          <Select value={entryType} onValueChange={setEntryType}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="ledger-entry-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entries</SelectItem>
              {Object.entries(ENTRY_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button onClick={() => setPayOpen(true)} disabled={!vendorId} className="h-9 bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="ledger-record-payment">
            <IndianRupee className="h-4 w-4" /> Record Payment
          </Button>
          <Button onClick={() => setNoteOpen("debit")} disabled={!vendorId} variant="outline" className="h-9 gap-1.5" data-testid="ledger-debit-note">
            <FileMinus className="h-4 w-4" /> Debit Note
          </Button>
          <Button onClick={() => setNoteOpen("credit")} disabled={!vendorId} variant="outline" className="h-9 gap-1.5" data-testid="ledger-credit-note">
            <FilePlus className="h-4 w-4" /> Credit Note
          </Button>
        </div>
      </Surface>

      {!vendorId && (
        <Surface className="text-center py-12 text-gray-400">
          <Wallet className="h-10 w-10 mx-auto mb-2 opacity-40" />
          Pick a vendor above to see their full accounts-payable ledger, outstanding invoices, and payment history.
        </Surface>
      )}

      {vendorId && balance && (
        <>
          {/* ---------- Summary tiles ---------- */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendor-ledger-summary">
            <SummaryTile label="Opening Balance" value={balance.opening} sub={balance.opening_type === "receivable" ? "Advance paid" : "Payable"} icon={<Wallet className="h-4 w-4" />} tone="slate" />
            <SummaryTile label="Purchases (GRN)" value={balance.purchases} sub="Credited to vendor" icon={<Receipt className="h-4 w-4" />} tone="amber" />
            <SummaryTile label="Payments Made" value={balance.payments} sub="Debited to vendor" icon={<TrendingDown className="h-4 w-4" />} tone="emerald" />
            <SummaryTile label="Current Balance" value={balance.current_balance} sub={balance.current_balance >= 0 ? "Payable to vendor" : "Vendor owes us"} icon={<TrendingUp className="h-4 w-4" />} tone={balance.current_balance > 0 ? "red" : "emerald"} big />
          </div>

          {/* ---------- Ledger + Outstanding side by side ---------- */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Ledger */}
            <Surface padded={false} className="lg:col-span-2">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-800">Ledger Entries</h3>
                <span className="text-xs text-gray-500 tabular-nums">
                  {ledger.total.toLocaleString()} row{ledger.total !== 1 ? "s" : ""} · Closing:
                  <span className={`ml-1 font-semibold ${ledger.closing_balance >= 0 ? "text-gray-900" : "text-emerald-700"}`}>
                    {formatINR(Math.abs(ledger.closing_balance))} {ledger.closing_balance >= 0 ? "Cr" : "Dr"}
                  </span>
                </span>
              </div>
              <div className="overflow-auto max-h-[calc(100vh-380px)]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Particulars</th>
                      <th className="px-3 py-2 text-right">Debit</th>
                      <th className="px-3 py-2 text-right">Credit</th>
                      <th className="px-3 py-2 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading && <tr><td colSpan={6} className="text-center py-6 text-gray-400">Loading…</td></tr>}
                    {!loading && ledger.items.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-10 text-gray-400">No ledger entries yet</td></tr>
                    )}
                    {ledger.items.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50" data-testid={`ledger-row-${r.id}`}>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(r.entry_date)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${ENTRY_TYPE_TONE[r.entry_type] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
                            {ENTRY_TYPE_LABEL[r.entry_type] || r.entry_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-800">{r.particulars}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-800">{r.debit ? formatINR(r.debit) : "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-amber-800">{r.credit ? formatINR(r.credit) : "—"}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.running_balance >= 0 ? "text-gray-900" : "text-emerald-700"}`}>
                          {formatINR(Math.abs(r.running_balance))} <span className="text-[10px] text-gray-400 font-normal">{r.running_balance >= 0 ? "Cr" : "Dr"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Surface>

            {/* Outstanding invoices */}
            <Surface padded={false}>
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-800">Outstanding Invoices</h3>
                <span className="text-xs text-gray-500 tabular-nums" data-testid="outstanding-count">{outstanding.length}</span>
              </div>
              <div className="overflow-auto max-h-[calc(100vh-380px)] p-2 space-y-2">
                {outstanding.length === 0 && (
                  <p className="text-center py-6 text-xs text-gray-400">All invoices settled</p>
                )}
                {outstanding.map((inv) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const isOverdue = inv.due_date && inv.due_date < today;
                  return (
                  <div key={inv.id} className={`rounded-md border p-2.5 hover:bg-gray-50 ${isOverdue ? "border-red-300 bg-red-50/40" : "border-gray-200"}`} data-testid={`outstanding-${inv.id}`}>
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span className="font-mono text-[#9B1C1C] font-semibold">{inv.purchase_no}</span>
                      <span>{formatDate(inv.purchase_date)}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">Inv {inv.invoice_no}</p>
                    {inv.due_date && (
                      <p className={`text-[11px] mt-0.5 font-medium ${isOverdue ? "text-red-700" : "text-gray-600"}`}>
                        Due: {inv.due_date}{inv.credit_period_days ? ` (${inv.credit_period_days}d)` : ""}
                        {isOverdue && <span className="ml-1 uppercase tracking-widest text-[10px]">Overdue</span>}
                      </p>
                    )}
                    <div className="flex items-baseline justify-between mt-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-gray-400">Outstanding</span>
                      <span className="text-sm font-bold tabular-nums text-red-700">{formatINR(inv.outstanding)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>Total {formatINR(inv.net_amount)}</span>
                      <span>Paid {formatINR(inv.paid_amount)}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </Surface>
          </div>
        </>
      )}

      {payOpen && (
        <PaymentDialog
          vendor={vendors.find((v) => v.id === vendorId)}
          outstanding={outstanding}
          onClose={() => setPayOpen(false)}
          onSaved={() => { setPayOpen(false); load(); }}
        />
      )}
      {noteOpen && (
        <NoteDialog
          vendor={vendors.find((v) => v.id === vendorId)}
          type={noteOpen}
          outstanding={outstanding}
          onClose={() => setNoteOpen(null)}
          onSaved={() => { setNoteOpen(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Summary tile ----------
function SummaryTile({ label, value, sub, icon, tone = "slate", big = false }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    red: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <Surface className={`!p-3 border ${tones[tone]}`} data-testid={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
        {icon} {label}
      </div>
      <p className={`mt-1 tabular-nums font-bold ${big ? "text-2xl" : "text-xl"}`}>
        {value < 0 ? "-" : ""}{formatINR(Math.abs(value))}
      </p>
      <p className="text-[11px] opacity-70">{sub}</p>
    </Surface>
  );
}

// =========================================================================
// PAYMENT DIALOG
// =========================================================================
function PaymentDialog({ vendor, outstanding, onClose, onSaved }) {
  const [modes, setModes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    payment_date: todayISO(),
    amount: "",
    mode: "Cash",
    ref_no: "",
    notes: "",
  });
  // allocations map keyed by grn_id
  const [allocs, setAllocs] = useState({});

  useEffect(() => {
    api.get("/production/rm-vendor-payment-modes").then((r) => setModes(r.data || []));
  }, []);

  const totalOutstanding = useMemo(
    () => outstanding.reduce((s, i) => s + (i.outstanding || 0), 0),
    [outstanding]
  );
  const amountNum = Number(form.amount) || 0;
  const allocated = useMemo(
    () => Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0),
    [allocs]
  );
  const unallocated = Math.max(0, amountNum - allocated);
  const overAllocated = allocated > amountNum + 0.01;

  const autoAllocate = () => {
    // Walk outstanding invoices oldest-first, greedy fill until amount runs out.
    let left = amountNum;
    const next = {};
    for (const inv of outstanding) {
      if (left <= 0) break;
      const take = Math.min(left, inv.outstanding);
      if (take > 0) {
        next[inv.id] = round2(take);
        left = round2(left - take);
      }
    }
    setAllocs(next);
  };

  const save = async () => {
    if (!vendor) return;
    if (amountNum <= 0) return toast.error("Enter a payment amount");
    if (overAllocated) return toast.error("Allocations exceed payment amount");
    const payload = {
      vendor_id: vendor.id,
      payment_date: form.payment_date,
      amount: amountNum,
      mode: form.mode,
      ref_no: form.ref_no || null,
      notes: form.notes || null,
      allocations: Object.entries(allocs)
        .filter(([, v]) => Number(v) > 0)
        .map(([grn_id, v]) => ({ grn_id, amount: Number(v) })),
    };
    try {
      setSaving(true);
      const r = await api.post("/production/rm-vendor-payments", payload);
      toast.success(`${r.data.payment_no} recorded`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-emerald-700" /> Record Payment · {vendor?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Payment Date *">
            <input type="date" value={form.payment_date} onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} className="h-9 rounded-md border border-gray-200 px-2 text-sm w-full" data-testid="pay-date" />
          </Field>
          <Field label="Amount *">
            <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0" className="h-9 rounded-md border border-gray-200 px-2 text-sm w-full text-right tabular-nums" data-testid="pay-amount" />
          </Field>
          <Field label="Mode *">
            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
              <SelectTrigger className="h-9" data-testid="pay-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reference #">
            <Input value={form.ref_no} onChange={(e) => setForm((f) => ({ ...f, ref_no: e.target.value }))} placeholder="Cheque / UPI / Txn ref" className="h-9" data-testid="pay-ref" />
          </Field>
          <Field label="Notes" className="col-span-2 md:col-span-4">
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional" className="h-9" data-testid="pay-notes" />
          </Field>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-600">Invoice Allocation</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Outstanding: <span className="font-semibold">{formatINR(totalOutstanding)}</span></span>
              <Button size="sm" variant="outline" onClick={autoAllocate} className="h-7 text-xs" data-testid="pay-auto-allocate">Auto-fill (oldest first)</Button>
            </div>
          </div>
          <div className="border border-gray-200 rounded-md max-h-[280px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2">GRN #</th>
                  <th className="px-3 py-2">Invoice</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Outstanding</th>
                  <th className="px-3 py-2 text-right w-32">Allocate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {outstanding.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-gray-400 text-xs">No outstanding invoices — payment will be on-account</td></tr>}
                {outstanding.map((inv) => (
                  <tr key={inv.id} data-testid={`pay-alloc-row-${inv.id}`}>
                    <td className="px-3 py-1.5 font-mono text-xs text-[#9B1C1C]">{inv.purchase_no}</td>
                    <td className="px-3 py-1.5 text-xs">{inv.invoice_no}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-600">{formatDate(inv.purchase_date)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-red-700 font-medium">{formatINR(inv.outstanding)}</td>
                    <td className="px-3 py-1.5">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={inv.outstanding}
                        value={allocs[inv.id] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const num = Math.min(Number(v) || 0, inv.outstanding);
                          setAllocs((a) => ({ ...a, [inv.id]: v === "" ? "" : num }));
                        }}
                        className="h-7 w-full rounded border border-gray-200 px-1.5 text-right text-sm tabular-nums"
                        data-testid={`pay-alloc-input-${inv.id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span>Payment: <span className="font-semibold tabular-nums">{formatINR(amountNum)}</span></span>
            <span>Allocated: <span className={`font-semibold tabular-nums ${overAllocated ? "text-red-700" : "text-gray-900"}`}>{formatINR(allocated)}</span></span>
            <span>Unallocated (on-account): <span className="font-semibold tabular-nums text-emerald-700">{formatINR(unallocated)}</span></span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pay-cancel">Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={saving || amountNum <= 0} data-testid="pay-save">
            {saving ? "Saving…" : "Save Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// DEBIT / CREDIT NOTE DIALOG
// =========================================================================
function NoteDialog({ vendor, type, outstanding, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    note_date: todayISO(),
    amount: "",
    against_grn_id: null,
    reason: "",
    notes: "",
  });
  const isDebit = type === "debit";

  const save = async () => {
    if (!vendor) return;
    if (!form.reason.trim()) return toast.error("Reason is required");
    if (!(Number(form.amount) > 0)) return toast.error("Amount must be positive");
    try {
      setSaving(true);
      const r = await api.post("/production/rm-vendor-notes", {
        vendor_id: vendor.id,
        note_type: type,
        note_date: form.note_date,
        amount: Number(form.amount),
        against_grn_id: form.against_grn_id || null,
        reason: form.reason,
        notes: form.notes || null,
      });
      toast.success(`${r.data.note_no} saved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid={`note-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDebit ? <FileMinus className="h-4 w-4 text-blue-700" /> : <FilePlus className="h-4 w-4 text-fuchsia-700" />}
            {isDebit ? "Debit Note" : "Credit Note"} · {vendor?.name}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-gray-500 -mt-1">
          {isDebit
            ? "Reduces what you owe the vendor. Used for returns, damaged goods, or price corrections in your favour."
            : "Increases what you owe the vendor. Used for short-billing, extra receipts, or corrections in the vendor's favour."}
        </p>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Date *">
            <input type="date" value={form.note_date} onChange={(e) => setForm((f) => ({ ...f, note_date: e.target.value }))} className="h-9 rounded-md border border-gray-200 px-2 text-sm w-full" data-testid="note-date" />
          </Field>
          <Field label="Amount *">
            <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0" className="h-9 rounded-md border border-gray-200 px-2 text-sm w-full text-right tabular-nums" data-testid="note-amount" />
          </Field>
          <Field label="Against GRN (optional)" className="col-span-2">
            <ComboBoxSearch
              value={form.against_grn_id}
              onChange={(v) => setForm((f) => ({ ...f, against_grn_id: v }))}
              options={outstanding.map((inv) => ({ value: inv.id, label: `${inv.purchase_no} · ${inv.invoice_no}`, sub: `Outstanding: ${formatINR(inv.outstanding)}` }))}
              placeholder="No GRN linked"
              clearable
              testId="note-grn"
            />
          </Field>
          <Field label="Reason *" className="col-span-2">
            <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Damaged pieces returned" className="h-9" data-testid="note-reason" />
          </Field>
          <Field label="Notes" className="col-span-2">
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional" className="h-9" data-testid="note-notes" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="note-cancel">Cancel</Button>
          <Button className={isDebit ? "bg-blue-700 hover:bg-blue-800" : "bg-fuchsia-700 hover:bg-fuchsia-800"} onClick={save} disabled={saving} data-testid="note-save">
            {saving ? "Saving…" : `Save ${isDebit ? "Debit" : "Credit"} Note`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- helpers ----------
function Field({ label, className, children }) {
  return (
    <div className={className}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</p>
      {children}
    </div>
  );
}

function round2(v) {
  return Math.round((Number(v) || 0) * 100) / 100;
}
